import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEraser, faColumns, faRotateLeft, faTimes, faCog, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { Dropdown } from 'react-bootstrap';

const DatatableHeader = ({
  titletable,
  titleDisabled,
  searchEnabled,
  globalSearch,
  handleGlobalSearchChange,
  handleGlobalSearchKeyDown,
  handleClearFilters,
  columnFilters,
  sortBy,
  initialSortBy,
  headerActions,
  cols = [],
  toggleColumnVisibility,
  resetLayout,
  searchPosition = 'right'
}) => {
  const [showColumns, setShowColumns] = useState(false);
  const hasTitle = !titleDisabled && titletable;
  const showSearchOnLeft = searchPosition === 'left' && !hasTitle;

  const searchBar = searchEnabled && (
    <div className="d-flex align-items-center gap-2 datatable-global-search-container">
      <span className="small fw-semibold text-white">Buscar:</span>
      <div className="position-relative">
        <input
          type="text"
          value={globalSearch}
          onChange={handleGlobalSearchChange}
          onKeyDown={handleGlobalSearchKeyDown}
          className="form-control datatable-global-search-input"
          placeholder="Escribe para buscar..."
          draggable={false}
          onDragStart={e => e.preventDefault()}
          style={{ paddingRight: globalSearch ? '28px' : '12px' }}
        />
        {globalSearch && (
          <button
            type="button"
            className="btn btn-sm position-absolute"
            style={{ right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '0 6px', color: '#888', border: 'none', background: 'transparent' }}
            onClick={() => handleGlobalSearchChange({ target: { value: '' } })}
            title="Limpiar búsqueda"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
    </div>
  );

  const settingsGear = ((cols.length > 0 && toggleColumnVisibility) || resetLayout) && (
    <Dropdown align="end" onToggle={(isOpen) => { if (!isOpen) setShowColumns(false); }}>
      <Dropdown.Toggle 
        as="button" 
        className="btn d-flex align-items-center justify-content-center hover-shadow datatable-clear-btn datatable-settings-toggle"
        title="Configurar tabla"
        style={{ cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', width: '44px', height: '44px', padding: 0 }}
      >
        <FontAwesomeIcon icon={faCog} className="datatable-gear-icon" style={{ fontSize: '18px' }} />
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: '240px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 0' }}>
        {cols.length > 0 && toggleColumnVisibility && (
          <>
            <Dropdown.Item 
              as="button"
              onClick={(e) => { e.stopPropagation(); setShowColumns(!showColumns); }}
              className="d-flex align-items-center justify-content-between py-2"
              style={{ fontSize: '0.9rem' }}
            >
              <span>
                <FontAwesomeIcon icon={faColumns} className="me-2 text-muted" />
                Columnas
              </span>
              <FontAwesomeIcon icon={showColumns ? faChevronUp : faChevronDown} style={{ fontSize: '0.8rem', color: '#888' }} />
            </Dropdown.Item>
            {showColumns && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px 8px', backgroundColor: '#f8f9fa', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                {cols.map(col => (
                  <button 
                    key={col.key} 
                    onClick={(e) => { e.stopPropagation(); toggleColumnVisibility(col.key); }}
                    className="d-flex align-items-center px-2 py-1 dropdown-item"
                    style={{ cursor: 'pointer', fontSize: '0.85rem', borderRadius: '4px' }}
                  >
                    <input 
                      type="checkbox" 
                      className="me-2 form-check-input mt-0" 
                      checked={col.isVisible !== false} 
                      onChange={() => {}}
                      style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                    />
                    <span style={{ userSelect: 'none' }}>{col.label}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {resetLayout && (
          <>
            {cols.length > 0 && toggleColumnVisibility && <Dropdown.Divider style={{ margin: '4px 0' }} />}
            <Dropdown.Item 
              as="button" 
              onClick={(e) => { e.stopPropagation(); resetLayout(); }}
              className="text-danger d-flex align-items-center py-2"
              style={{ fontSize: '0.9rem' }}
            >
              <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
              Restablecer diseño
            </Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center w-100 px-2">
      <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-start gap-2">
        {hasTitle && (
          <h5 className="mb-0 text-white datatable-title text-center text-md-start">{titletable}</h5>
        )}
        {showSearchOnLeft && searchBar}
        {showSearchOnLeft && settingsGear}
      </div>
      <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-end gap-2">
        {headerActions}
        {(globalSearch || Object.keys(columnFilters).some(k => columnFilters[k]) || (sortBy !== (initialSortBy || ''))) && (
          <button
            className="btn d-flex align-items-center justify-content-center hover-shadow datatable-clear-btn"
            onClick={handleClearFilters}
            title="Limpiar filtros y búsqueda"
          >
            <FontAwesomeIcon icon={faEraser} className="me-2" />
            Limpiar Filtros
          </button>
        )}
        {/* Dropdown de columnas anterior eliminado */}
        {!showSearchOnLeft && searchBar}
        {!showSearchOnLeft && settingsGear}
      </div>
    </div>
  );
};

DatatableHeader.propTypes = {
  titletable: PropTypes.string,
  titleDisabled: PropTypes.bool,
  searchEnabled: PropTypes.bool,
  globalSearch: PropTypes.string,
  handleGlobalSearchChange: PropTypes.func,
  handleGlobalSearchKeyDown: PropTypes.func,
  handleClearFilters: PropTypes.func,
  columnFilters: PropTypes.object,
  sortBy: PropTypes.string,
  initialSortBy: PropTypes.string,
  headerActions: PropTypes.node,
  cols: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node,
      isVisible: PropTypes.bool,
    })
  ),
  toggleColumnVisibility: PropTypes.func,
  resetLayout: PropTypes.func,
  searchPosition: PropTypes.oneOf(['left', 'right']),
};

export default DatatableHeader;
